import { Image, Modal, type MantineRadius } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import type { CSSProperties } from "react";

interface ImageZoomProps {
  src: string;
  alt?: string;
  w?: number | string;
  h?: number | string;
  fit?: CSSProperties["objectFit"];
  radius?: MantineRadius;
  withBorder?: boolean;
  background?: string;
  /** Click to open a full-screen view. Defaults to true. */
  zoom?: boolean;
}

/**
 * Unified image surface used for previews, thumbnails and detail images.
 * When `zoom` is on, clicking opens a centered full-screen modal.
 */
export default function ImageZoom({
  src,
  alt,
  w,
  h,
  fit = "cover",
  radius = "md",
  withBorder = true,
  background,
  zoom = true,
}: ImageZoomProps) {
  const [opened, { open, close }] = useDisclosure(false);

  return (
    <>
      <Image
        src={src}
        alt={alt}
        w={w}
        h={h}
        radius={radius}
        onClick={zoom ? open : undefined}
        style={{
          objectFit: fit,
          background,
          cursor: zoom ? "zoom-in" : undefined,
          border: withBorder ? "1px solid var(--mantine-color-default-border)" : undefined,
          display: "block",
        }}
      />
      {zoom && (
        <Modal opened={opened} onClose={close} size="xl" padding={0} withCloseButton={false} overlayProps={{ blur: 4 }}>
          <Image
            src={src}
            alt={alt}
            fit="contain"
            style={{ maxHeight: "85vh", cursor: "zoom-out" }}
            onClick={close}
          />
        </Modal>
      )}
    </>
  );
}
